import { blockScrollOnBody, releaseScrollOnBody } from 'utils/body-lock'
import { watchBreakpoint } from 'utils/breakpoints'
import { watchElementScrolledPast } from 'utils/viewport'
import { sessionStorageService } from 'services/storage'
import { pushDataLayer } from 'utils/data-layer-push'
import { initExitPopupFormControls } from './form-handling'
import { ExitPopupOptions } from 'models/exit-popup'

const EXIT_POPUP_SESSION_KEY = 'wt_exitpopup_shown'
// Same form name as the `form_submitted` event of the landing registration form, so the funnel can be joined.
const EXIT_POPUP_FORM_NAME = 'Rejstracja z landingu'
const MOUSE_LEAVE_DELAY = 10 * 1000
const DESKTOP_BREAKPOINT_NAME = 'big'
const FORM_FIELD_TAGS = ['INPUT', 'TEXTAREA', 'SELECT']

/** Returns `true` when the popup is allowed to be shown automatically. */
type DisplayCondition = () => boolean

/** Starts watching for one automatic trigger; everything it registers must be released on `signal` abort. */
type AutomaticTrigger = (onTrigger: () => void, signal: AbortSignal) => void

const isNotShownInSession: DisplayCondition = () => sessionStorageService.getItem(EXIT_POPUP_SESSION_KEY) !== 'true'

const createIsFormUntouched = (): DisplayCondition => {
  const controller = new AbortController()
  let hasInteracted = false

  const handleInteraction = (event: Event): void => {
    const { target } = event
    if (target instanceof HTMLElement && FORM_FIELD_TAGS.includes(target.tagName)) {
      hasInteracted = true
      controller.abort()
    }
  }

  document.addEventListener('focusin', handleInteraction, { signal: controller.signal })
  document.addEventListener('change', handleInteraction, { signal: controller.signal })

  return () => !hasInteracted
}

/**
 * ALL of these must be met for an automatic trigger to open the popup.
 * Add, remove or comment out a line to change when the popup may appear.
 * (Clicking `[data-open-exit-popup]` ignores them.)
 */
const createDisplayConditions = (): DisplayCondition[] => [
  isNotShownInSession, // at most once per browser session
  createIsFormUntouched(), // user has not touched any form field
]

/** Main page form (`[data-main-form]`) has been seen and then scrolled out of the viewport upwards. */
const watchMainFormScrolledPast: AutomaticTrigger = (onTrigger, signal) => {
  const $mainForm = document.querySelector<HTMLElement>('[data-main-form]')

  if ($mainForm) {
    watchElementScrolledPast($mainForm, onTrigger, signal)
  }
}

/** Cursor leaves the window (only after `MOUSE_LEAVE_DELAY`). */
const watchMouseLeave: AutomaticTrigger = (onTrigger, signal) => {
  const timeoutId = window.setTimeout(() => {
    document.body.addEventListener('mouseleave', onTrigger, { once: true, signal })
  }, MOUSE_LEAVE_DELAY)

  signal.addEventListener('abort', () => clearTimeout(timeoutId))
}

/**
 * ANY of these may open the popup (if all display conditions are met).
 * Add, remove or comment out a line to change what opens the popup.
 */
const AUTOMATIC_TRIGGERS: AutomaticTrigger[] = [watchMainFormScrolledPast, watchMouseLeave]

interface PopupElements {
  $popup: HTMLElement
  $closeButton: HTMLButtonElement
  $openButtons: NodeListOf<HTMLButtonElement>
}

const findPopupElements = (): PopupElements | null => {
  const $popup = document.querySelector<HTMLElement>('[data-exit-popup]')
  const $closeButton = document.querySelector<HTMLButtonElement>('[data-close-exit-popup]')
  const $openButtons = document.querySelectorAll<HTMLButtonElement>('[data-open-exit-popup]')

  if (!$popup || !$closeButton) {
    console.warn('ExitPopup: Could not find the popup element or the close button.')
    return null
  }

  return { $popup, $closeButton, $openButtons }
}

const createPopupActions = ($popup: HTMLElement) => {
  const open = (): void => {
    $popup.hidden = false
    blockScrollOnBody()
    sessionStorageService.setItem(EXIT_POPUP_SESSION_KEY, 'true')
    pushDataLayer('popup_view', { form_name: EXIT_POPUP_FORM_NAME })
  }

  const close = (): void => {
    $popup.hidden = true
    releaseScrollOnBody()
  }

  const handleOpenClick = (event: MouseEvent): void => {
    event.preventDefault()
    open()
  }

  const handleCloseClick = (): void => {
    close()
    pushDataLayer('click_button', { button_type: 'Button', button_text: 'Zamknięcie popupu rejestracji z landingu' })
  }

  return { open, close, handleOpenClick, handleCloseClick }
}

type PopupActions = ReturnType<typeof createPopupActions>

const createOpenIfConditionsMet = (open: () => void): (() => void) => {
  const displayConditions = createDisplayConditions()

  return () => {
    if (displayConditions.every((isMet) => isMet())) {
      open()
    }
  }
}

const bindClickTriggers = (
  { $closeButton, $openButtons }: PopupElements,
  { handleCloseClick, handleOpenClick }: PopupActions,
  signal: AbortSignal,
): void => {
  $closeButton.addEventListener('click', handleCloseClick, { signal })
  $openButtons.forEach(($button) => $button.addEventListener('click', handleOpenClick, { signal }))
}

const bindAutomaticTriggers = (onTrigger: () => void, signal: AbortSignal): void => {
  AUTOMATIC_TRIGGERS.forEach((startWatching) => startWatching(onTrigger, signal))
}

/** `enable` runs `bind` (it registers listeners on the given signal), `disable` removes them all and calls `onDisable`. */
const createToggleableListeners = (bind: (signal: AbortSignal) => void, onDisable: () => void) => {
  let controller: AbortController | null = null

  return {
    enable: (): void => {
      if (!controller) {
        controller = new AbortController()
        bind(controller.signal)
      }
    },
    disable: (): void => {
      if (controller) {
        controller.abort()
        controller = null
        onDisable()
      }
    },
  }
}

/** Without `disableOnMobile` the triggers are always on; with it they follow the desktop breakpoint. */
const toggleByViewport = (disableOnMobile: boolean, enable: () => void, disable: () => void): void => {
  if (disableOnMobile) {
    watchBreakpoint(DESKTOP_BREAKPOINT_NAME, enable, disable)
  } else {
    enable()
  }
}

/**
 * Initializes and manages an exit-intent popup.
 *
 * It can be opened by clicking an element with `[data-open-exit-popup]` (always), or by an
 * automatic trigger (see `AUTOMATIC_TRIGGERS`) provided all display conditions are met
 * (see `createDisplayConditions`).
 *
 * @param {ExitPopupOptions} [options] - Configuration options, see `ExitPopupOptions`.
 */
const initExitPopup = (options: ExitPopupOptions = {}): void => {
  const {
    isOnlyClickListener = false,
    disableOnMobile = false,
    bindExitPopupForm = initExitPopupFormControls,
  } = options

  const popupElements = findPopupElements()
  if (!popupElements) {
    return
  }

  bindExitPopupForm()

  const popupActions = createPopupActions(popupElements.$popup)
  const openIfConditionsMet = createOpenIfConditionsMet(popupActions.open)

  const { enable, disable } = createToggleableListeners((signal) => {
    bindClickTriggers(popupElements, popupActions, signal)
    if (!isOnlyClickListener) {
      bindAutomaticTriggers(openIfConditionsMet, signal)
    }
  }, popupActions.close)

  toggleByViewport(disableOnMobile, enable, disable)
}

export default initExitPopup
